/* eslint-disable */
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { getIn } from 'formik';
import ElegantSwitch from 'components/Shared/ElegantSwitch';

const useStyles = makeStyles(theme => ({
  card: {
    border: '2px solid #e0e0e0',
    borderRadius: 12,
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: theme.colors?.lightBlue || '#5B65DC',
      boxShadow: '0 4px 12px rgba(0, 168, 255, 0.1)',
    },
  },
  cardActive: {
    borderColor: theme.colors?.lightBlue || '#5B65DC',
    backgroundColor: '#f8fbff',
    boxShadow: '0 2px 8px rgba(0, 168, 255, 0.15)',
  },
  cardDisabled: {
    backgroundColor: '#f5f5f5',
    borderColor: '#d0d0d0',
    boxShadow: '0 0',
    '&:hover': {
      borderColor: '#d0d0d0',
      boxShadow: '0 0',
    },
    '& *': {
      color: '#999 !important',
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    fontSize: '0.9rem',
  },
  quantityContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  quantityButton: {
    width: 30,
    height: 30,
    backgroundColor: theme.colors?.lightBlue || '#5B65DC',
    color: 'white',
    '&:hover': {
      backgroundColor: theme.colors?.blue2 || '#122056',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
      color: '#999',
    },
  },
  quantityInput: {
    '& .MuiOutlinedInput-root': {
      width: 60,
      height: 32,
      borderRadius: 6,
      '& input': {
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '0.9rem',
        padding: theme.spacing(0.5),
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors?.lightBlue || '#5B65DC',
    },
  },
  disabledText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    fontSize: '1.2rem',
    color: '#999',
    fontWeight: 600,
  },
  switchContainer: {
    margin: 0,
  },
  switchLabel: {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
}));

const QuantitySelector = ({ name, checkBoxName, title, isEditable, backendError, form }) => {
  const classes = useStyles();
  const isChecked = form.values[checkBoxName];
  const quantity = form.values[name] || 1;
  const error = getIn(form.errors, name) || backendError;

  const handleIncrement = () => {
    form.setFieldValue(name, quantity + 1);
  };

  const handleDecrement = () => {
    form.setFieldValue(name, Math.max(1, quantity - 1));
  };

  const handleQuantityChange = e => {
    const value = parseInt(e.target.value) || 1;
    if (value >= 1) {
      form.setFieldValue(name, value);
    }
  };

  const handleSwitchChange = checked => {
    form.setFieldValue(checkBoxName, checked);
    if (checked && !form.values[name]) {
      form.setFieldValue(name, 1);
    }
  };

  return (
    <Card
      className={`${classes.card} ${isChecked ? classes.cardActive : ''} ${
        !isEditable ? classes.cardDisabled : ''
      }`}
    >
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.leftSection}>
            <Typography className={classes.title}>{title}</Typography>
            <FormControlLabel
              control={
                <ElegantSwitch
                  checked={isChecked}
                  onChange={e => handleSwitchChange(e.target.checked)}
                  disabled={!isEditable}
                />
              }
              label=""
              className={classes.switchContainer}
            />
          </div>

          <div className={classes.quantityContainer}>
            {isChecked ? (
              <>
                <IconButton
                  className={classes.quantityButton}
                  onClick={handleDecrement}
                  disabled={!isEditable || quantity <= 1}
                  size="small"
                >
                  <Remove />
                </IconButton>

                <TextField
                  value={quantity}
                  onChange={handleQuantityChange}
                  disabled={!isEditable}
                  variant="outlined"
                  className={classes.quantityInput}
                  inputProps={{
                    min: 1,
                    type: 'number',
                  }}
                  error={!!error}
                />

                <IconButton
                  className={classes.quantityButton}
                  onClick={handleIncrement}
                  disabled={!isEditable}
                  size="small"
                >
                  <Add />
                </IconButton>
              </>
            ) : (
              <div className={classes.disabledText}>—</div>
            )}
          </div>
        </div>

        {error && (
          <Typography variant="caption" color="error" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

QuantitySelector.propTypes = {
  name: PropTypes.string.isRequired,
  checkBoxName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  backendError: PropTypes.string,
  form: PropTypes.object.isRequired,
};

QuantitySelector.defaultProps = {
  isEditable: true,
  backendError: null,
};

export default QuantitySelector;
